import type { ReactNode } from 'react';
import { STAGE_LABELS, type EngagementStage } from '../../shared/stages';
import { BAND_LABELS, type PriorityBand } from '../../shared/scoring';

export function StagePill({ stage }: { stage: string }) {
  const palette: Record<string, string> = {
    new_inquiry: 'border-sky-700 bg-sky-950/60 text-sky-300',
    auto_qualified: 'border-emerald-700 bg-emerald-950/60 text-emerald-300',
    needs_review: 'border-amber-700 bg-amber-950/60 text-amber-300',
    discovery_scheduled: 'border-cyan-700 bg-cyan-950/60 text-cyan-300',
    assessment_in_progress: 'border-violet-700 bg-violet-950/60 text-violet-300',
    roadmap_delivered: 'border-indigo-700 bg-indigo-950/60 text-indigo-300',
    proposal_sent: 'border-fuchsia-700 bg-fuchsia-950/60 text-fuchsia-300',
    implementation_active: 'border-teal-700 bg-teal-950/60 text-teal-300',
    closed_won: 'border-green-600 bg-green-950/60 text-green-300',
    closed_lost: 'border-slate-700 bg-slate-900 text-slate-400',
  };
  return (
    <span className={`chip ${palette[stage] ?? 'border-slate-700 bg-slate-900 text-slate-300'}`}>
      {STAGE_LABELS[stage as EngagementStage] ?? stage}
    </span>
  );
}

export function BandChip({ band }: { band: string | null }) {
  if (!band) return <span className="chip border-slate-700 text-slate-400">—</span>;
  const palette: Record<string, string> = {
    quick_win: 'border-green-600 bg-green-950/60 text-green-300',
    strategic_bet: 'border-indigo-600 bg-indigo-950/60 text-indigo-300',
    governance_first: 'border-amber-600 bg-amber-950/60 text-amber-300',
    evaluate: 'border-slate-600 bg-slate-900 text-slate-300',
    defer: 'border-slate-700 bg-slate-950 text-slate-500',
  };
  return (
    <span className={`chip ${palette[band] ?? 'border-slate-700 text-slate-300'}`}>
      {BAND_LABELS[band as PriorityBand] ?? band}
    </span>
  );
}

export function RiskTierChip({ tier, decision }: { tier: string | null; decision?: string | null }) {
  if (!tier) return null;
  const palette: Record<string, string> = {
    low: 'border-green-700 bg-green-950/50 text-green-300',
    medium: 'border-amber-700 bg-amber-950/50 text-amber-300',
    high: 'border-red-700 bg-red-950/50 text-red-300',
  };
  return (
    <span className={`chip ${palette[tier] ?? ''}`}>
      {tier} risk{tier === 'high' ? (decision === 'go' ? ' · GO ✓' : decision === 'no_go' ? ' · NO-GO' : ' · decision needed') : ''}
    </span>
  );
}

export function ReviewChip({ status }: { status: string }) {
  const palette: Record<string, string> = {
    draft: 'border-slate-600 bg-slate-900 text-slate-300',
    in_review: 'border-amber-600 bg-amber-950/50 text-amber-300',
    approved: 'border-green-600 bg-green-950/50 text-green-300',
  };
  const label: Record<string, string> = { draft: 'Draft', in_review: 'In review', approved: 'Approved' };
  return <span className={`chip ${palette[status] ?? ''}`}>{label[status] ?? status}</span>;
}

export function ScoreDonut({ score, label }: { score: number | null; label: string }) {
  const v = score ?? 0;
  const color = v >= 70 ? 'text-green-400' : v >= 40 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="flex flex-col items-center rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2">
      <span className={`text-xl font-bold ${score == null ? 'text-slate-600' : color}`}>{score ?? '—'}</span>
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    </div>
  );
}

export function HeatCell({ score }: { score: number | null }) {
  const palette = ['bg-red-900/70', 'bg-red-800/60', 'bg-amber-800/60', 'bg-amber-600/50', 'bg-green-700/60', 'bg-green-500/60'];
  return (
    <div
      className={`flex h-9 w-9 items-center justify-center rounded-md border border-slate-800 text-sm font-bold ${
        score == null ? 'bg-slate-900 text-slate-600' : `${palette[score]} text-slate-50`
      }`}
    >
      {score ?? '·'}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-slate-400">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-700 border-t-brand-400" />
      {label ?? 'Loading…'}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="my-3 rounded-lg border border-red-900 bg-red-950/40 px-4 py-2 text-sm text-red-300">
      {message}
    </div>
  );
}

export function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-base font-bold text-slate-100">{children}</h2>
      {right}
    </div>
  );
}

export function fmtDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
